import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { releasesAPI, statsAPI, healthAPI, migrationAPI, exportAPI, backupAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Label } from '../components/ui/label';
import { Disc3, Plus, Search, LogOut, SortAsc, Filter, Library, DollarSign, User, CheckCircle2, XCircle, Crown, Copy, Zap, Sun, Moon, RefreshCw, LayoutGrid, List, Download, FileJson, FileSpreadsheet, SlidersHorizontal, Heart, Mail, Package, Upload, HelpCircle, Tag } from 'lucide-react';
import { toast } from 'sonner';
import ReleaseCard from '../components/ReleaseCard';
import AddAlbumModal from '../components/AddAlbumModal';
import { FullLogo } from '../components/Logo';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [collection, setCollection] = useState([]);
  const [stats, setStats] = useState({ total_releases: 0, total_copies: 0, total_value: null, currency: 'USD' });
  const [filters, setFilters] = useState({ genres: [], styles: [] });
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [discogsStatus, setDiscogsStatus] = useState({ ok: false, message: 'Checking...' });
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('artist');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [selectedStyle, setSelectedStyle] = useState('all');
  const [selectedCondition, setSelectedCondition] = useState('all');
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [refreshingValue, setRefreshingValue] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('vinyllog_view') || 'grid');
  const [exporting, setExporting] = useState(false);
  const [sendingBackup, setSendingBackup] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState(false);
  
  const fileInputRef = React.useRef(null);
  const isPro = user?.plan === 'PRO' || user?.plan === 'LIFETIME_PRO';

  // Condition options (ranked from best to worst)
  const conditionOptions = [
    { value: 'all', label: 'All Conditions' },
    { value: 'M', label: 'M (Mint) and up' },
    { value: 'NM', label: 'NM and up' },
    { value: 'VG+', label: 'VG+ and up' },
    { value: 'VG', label: 'VG and up' },
    { value: 'G+', label: 'G+ and up' },
    { value: 'G', label: 'G and up' },
  ];

  const handleExport = async (format) => {
    if (stats.total_copies === 0) {
      toast.info('No records to export');
      return;
    }
    
    setExporting(true);
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Fetch with proper headers
      const response = await fetch(`${apiUrl}/api/export/${format}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.status === 403) {
        toast.error('Export is a PRO feature. Upgrade to access.');
        return;
      }
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      // Get blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `vinyllog_export_${new Date().toISOString().slice(0,10)}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Collection exported as ${format.toUpperCase()}`);
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Could not export collection');
    } finally {
      setExporting(false);
    }
  };

  const handleEmailBackup = async () => {
    if (stats.total_copies === 0) {
      toast.info('No records to backup');
      return;
    }
    
    setSendingBackup(true);
    try {
      const res = await backupAPI.sendEmailBackup();
      if (res.data.success) {
        toast.success(res.data.message);
      } else if (res.data.demo_mode) {
        toast.error('Email service not configured. Please download backup manually.');
      } else {
        toast.error(res.data.message || 'Failed to send backup');
      }
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Email backup is a PRO feature. Upgrade to access.');
      } else {
        toast.error('Failed to send backup email');
      }
    } finally {
      setSendingBackup(false);
    }
  };

  const handleRestoreBackup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Reset file input
    event.target.value = '';
    
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a JSON backup file');
      return;
    }
    
    setRestoringBackup(true);
    try {
      const res = await backupAPI.restoreBackup(file);
      if (res.data.success) {
        toast.success(`Imported ${res.data.imported} records! ${res.data.skipped} skipped (already in collection)`);
        // Refresh collection
        fetchData();
      } else {
        toast.error(res.data.message || 'Failed to restore backup');
      }
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Restore backup is a PRO feature. Upgrade to access.');
      } else if (err.response?.status === 400) {
        toast.error(err.response?.data?.detail || 'Invalid backup file');
      } else {
        toast.error('Failed to restore backup');
      }
    } finally {
      setRestoringBackup(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const params = {
        sort_by: sortBy,
        sort_order: sortOrder,
        genre: selectedGenre === 'all' ? undefined : selectedGenre,
        style: selectedStyle === 'all' ? undefined : selectedStyle,
        min_condition: selectedCondition === 'all' ? undefined : selectedCondition,
        min_value: minValue ? parseFloat(minValue) : undefined,
        max_value: maxValue ? parseFloat(maxValue) : undefined,
      };
      
      const [collectionRes, statsRes, filtersRes] = await Promise.all([
        releasesAPI.getCollection(params),
        statsAPI.getStats(),
        releasesAPI.getFilters(),
      ]);
      setCollection(collectionRes.data);
      setStats(statsRes.data);
      setFilters(filtersRes.data);
    } catch (err) {
      // Try migration if collection is empty (first time after update)
      try {
        await migrationAPI.migrate();
        // Retry fetch after migration
        const [collectionRes, statsRes, filtersRes] = await Promise.all([
          releasesAPI.getCollection({ sort_by: sortBy, sort_order: sortOrder }),
          statsAPI.getStats(),
          releasesAPI.getFilters(),
        ]);
        setCollection(collectionRes.data);
        setStats(statsRes.data);
        setFilters(filtersRes.data);
      } catch {
        toast.error('Failed to load collection');
      }
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder, selectedGenre, selectedStyle, selectedCondition, minValue, maxValue]);

  const checkDiscogsStatus = useCallback(async () => {
    try {
      const res = await healthAPI.checkDiscogs();
      setDiscogsStatus(res.data);
    } catch (err) {
      setDiscogsStatus({ ok: false, message: 'Connection check failed' });
    }
  }, []);

  useEffect(() => {
    fetchData();
    checkDiscogsStatus();
  }, [fetchData, checkDiscogsStatus]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleAlbumAdded = () => {
    setShowAddModal(false);
    fetchData();
    toast.success('Copy added to collection!');
  };

  const handleRefreshValues = async () => {
    if (stats.total_releases === 0) {
      toast.info('Ingen records at opdatere');
      return;
    }
    
    setRefreshingValue(true);
    try {
      const res = await releasesAPI.refreshAllStats();
      toast.success(`Updated market values for ${res.data.updated_count} records`);
      // Refetch stats
      const statsRes = await statsAPI.getStats();
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Could not update market values');
    } finally {
      setRefreshingValue(false);
    }
  };

  const toggleViewMode = () => {
    const newMode = viewMode === 'grid' ? 'list' : 'grid';
    setViewMode(newMode);
    localStorage.setItem('vinyllog_view', newMode);
  };

  const filteredCollection = collection.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.release?.artist?.toLowerCase().includes(query) ||
      item.release?.title?.toLowerCase().includes(query)
    );
  });

  // Calculate total copies
  const totalCopies = collection.reduce((sum, item) => sum + item.copy_count, 0);

  return (
    <div className="min-h-screen bg-background">
      {/* Hidden file input for restore */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleRestoreBackup}
        accept=".json"
        className="hidden"
      />
      
      {/* Header */}
      <header className="sticky top-0 z-40 glass">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo-icon.svg" alt="Vinyllog" className="w-10 h-10" />
              <div className="flex flex-col">
                <h1 className="text-xl font-bold leading-tight">Vinyllog</h1>
                <span className="text-[10px] text-muted-foreground tracking-wide hidden sm:block">Catalog Your Collection</span>
              </div>
              <div className="hidden md:flex items-center gap-1.5 ml-4 px-2 py-1 rounded-sm bg-secondary text-xs">
                {discogsStatus.ok ? (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                    <span className="text-muted-foreground">Discogs connected</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-3.5 h-3.5 text-red-500" />
                    <span className="text-muted-foreground">Discogs disconnected</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              {/* Need Help Link */}
              <a
                href="https://vinyllog.app/support/"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <HelpCircle className="w-4 h-4" />
                <span>Need help?</span>
              </a>

              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="w-9 h-9"
                data-testid="theme-toggle-btn"
              >
                {theme === 'dark' ? (
                  <Sun className="w-4 h-4 text-primary" />
                ) : (
                  <Moon className="w-4 h-4 text-primary" />
                )}
                <span className="sr-only">Skift tema</span>
              </Button>

              {/* Wishlist Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/wishlist')}
                className="w-9 h-9"
                data-testid="wishlist-btn"
              >
                <Heart className="w-4 h-4 text-pink-500" />
                <span className="sr-only">Wishlist</span>
              </Button>

              {/* PRO Features Menu */}
              {isPro && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2 hidden md:flex" data-testid="pro-menu-btn">
                      <Crown className="w-4 h-4 text-amber-500" />
                      <span>PRO</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem 
                      onClick={() => navigate('/sales')}
                      className="gap-2"
                    >
                      <Tag className="w-4 h-4" />
                      For Sale
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/batch-import')}
                      className="gap-2"
                    >
                      <Package className="w-4 h-4" />
                      Batch Import
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleEmailBackup}
                      disabled={sendingBackup}
                      className="gap-2"
                    >
                      <Mail className="w-4 h-4" />
                      {sendingBackup ? 'Sending...' : 'Email Backup'}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => fileInputRef.current?.click()}
                      disabled={restoringBackup}
                      className="gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      {restoringBackup ? 'Restoring...' : 'Restore Backup'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => handleExport('json')}
                      disabled={exporting}
                      className="gap-2"
                    >
                      <FileJson className="w-4 h-4" />
                      Export JSON
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleExport('csv')}
                      disabled={exporting}
                      className="gap-2"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      Export CSV
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-btn">
                    <User className="w-4 h-4" />
                    <span className="hidden md:inline">{user?.name}</span>
                    {isPro && (
                      <Badge variant="secondary" className="ml-1 bg-primary/20 text-primary">
                        <Crown className="w-3 h-3 mr-1" />
                        {user?.plan === 'LIFETIME_PRO' ? 'LIFETIME' : 'PRO'}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="px-2 py-1.5 text-sm">
                    <p className="font-medium">{user?.email}</p>
                    <p className="text-muted-foreground text-xs">Plan: {user?.plan || 'FREE'}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/wishlist')} className="gap-2 md:hidden">
                    <Heart className="w-4 h-4 text-pink-500" />
                    Wishlist
                  </DropdownMenuItem>
                  <div className="md:hidden px-2 py-1.5 text-sm">
                    {discogsStatus.ok ? (
                      <span className="flex items-center gap-2 text-green-500">
                        <CheckCircle2 className="w-4 h-4" />
                        Discogs connected
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 text-red-500">
                        <XCircle className="w-4 h-4" />
                        Discogs disconnected
                      </span>
                    )}
                  </div>
                  <DropdownMenuSeparator className="md:hidden" />
                  {/* PRO features in mobile menu */}
                  {isPro && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/sales')} className="gap-2 md:hidden">
                        <Tag className="w-4 h-4" />
                        For Sale
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/batch-import')} className="gap-2 md:hidden">
                        <Package className="w-4 h-4" />
                        Batch Import
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleEmailBackup} disabled={sendingBackup} className="gap-2 md:hidden">
                        <Mail className="w-4 h-4" />
                        {sendingBackup ? 'Sending...' : 'Email Backup'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => fileInputRef.current?.click()} disabled={restoringBackup} className="gap-2 md:hidden">
                        <Upload className="w-4 h-4" />
                        {restoringBackup ? 'Restoring...' : 'Restore Backup'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExport('json')} disabled={exporting} className="gap-2 md:hidden">
                        <FileJson className="w-4 h-4" />
                        Export JSON
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="md:hidden" />
                    </>
                  )}
                  <DropdownMenuItem onClick={toggleTheme} data-testid="theme-toggle-menu">
                    {theme === 'dark' ? (
                      <>
                        <Sun className="w-4 h-4 mr-2" />
                        Skift til lyst tema
                      </>
                    ) : (
                      <>
                        <Moon className="w-4 h-4 mr-2" />
                        Switch to dark theme
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {/* Subscription management for PRO users */}
                  {isPro && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/upgrade')}>
                        <Crown className="w-4 h-4 mr-2 text-primary" />
                        Administrer abonnement
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem asChild className="md:hidden">
                    <a
                      href="https://vinyllog.app/support/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2"
                    >
                      <HelpCircle className="w-4 h-4" />
                      Need help?
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="md:hidden" />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        {/* Stats Cards - Compact */}
        <div className="grid grid-cols-3 gap-2 md:gap-4 mb-6">
          <div className="flex items-center gap-2 md:gap-3 p-2.5 md:p-4 rounded-lg bg-card border border-border">
            <Library className="w-4 md:w-5 h-4 md:h-5 text-primary flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] md:text-xs text-muted-foreground">Releases</p>
              <p className="text-base md:text-xl font-bold" data-testid="total-releases">{stats.total_releases}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3 p-2.5 md:p-4 rounded-lg bg-card border border-border">
            <Copy className="w-4 md:w-5 h-4 md:h-5 text-primary flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] md:text-xs text-muted-foreground">Copies</p>
              <p className="text-base md:text-xl font-bold" data-testid="total-copies">{totalCopies}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3 p-2.5 md:p-4 rounded-lg bg-card border border-border">
            <DollarSign className="w-4 md:w-5 h-4 md:h-5 text-primary flex-shrink-0" />
            <div className="flex-1 min-w-0 overflow-hidden">
              <div className="flex items-center gap-1">
                <p className="text-[10px] md:text-xs text-muted-foreground">Value</p>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleRefreshValues}
                  disabled={refreshingValue || stats.total_releases === 0}
                  className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0 -my-1"
                  title="Update market values"
                  data-testid="refresh-values-btn"
                >
                  <RefreshCw className={`w-3 h-3 text-muted-foreground ${refreshingValue ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <p className="text-base md:text-xl font-bold truncate" data-testid="total-value">
                {stats.total_value ? formatCurrency(stats.total_value, stats.currency) : '-'}
              </p>
            </div>
          </div>
        </div>

        {/* PRO Upgrade Banner (for FREE users) */}
        {!isPro && (
          <div 
            className="mb-8 p-4 rounded-sm bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 flex items-center justify-between cursor-pointer hover:border-primary/40 transition-colors"
            onClick={() => navigate('/upgrade')}
            data-testid="upgrade-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <Crown className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Upgrade to PRO</p>
                <p className="text-sm text-muted-foreground">
                  Batch import, export and matrix search from $2.99/mo
                </p>
              </div>
            </div>
            <Button size="sm" className="bg-primary text-primary-foreground gap-2">
              <Zap className="w-4 h-4" />
              View Plans
            </Button>
          </div>
        )}

        {/* Toolbar - Row 1: Search + Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search artist or album..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 bg-secondary border-border"
              data-testid="search-input"
            />
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={() => setShowAddModal(true)}
              className="h-10 gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="add-album-btn"
              disabled={!discogsStatus.ok}
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add</span>
            </Button>
            
            {/* Batch Import - PRO feature */}
            <Button
              onClick={() => isPro ? navigate('/batch-import') : navigate('/upgrade')}
              variant="outline"
              className="h-10 gap-2 relative"
              data-testid="batch-import-btn"
              disabled={!discogsStatus.ok}
            >
              <Zap className="w-4 h-4" />
              <span className="hidden sm:inline">Batch</span>
              {!isPro && (
                <Crown className="w-3 h-3 text-primary absolute -top-1 -right-1" />
              )}
            </Button>

            {/* Export Dropdown - PRO feature */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="h-10 gap-2 relative"
                  disabled={exporting || stats.total_copies === 0}
                  data-testid="export-btn"
                >
                  {exporting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Export</span>
                  {!isPro && (
                    <Crown className="w-3 h-3 text-primary absolute -top-1 -right-1" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isPro ? (
                  <>
                    <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-csv">
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Export as CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExport('json')} data-testid="export-json">
                      <FileJson className="w-4 h-4 mr-2" />
                      Export as JSON
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">
                      Export is a PRO feature
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/upgrade')}>
                      <Crown className="w-4 h-4 mr-2 text-primary" />
                      Upgrade to PRO
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Toolbar - Row 2: Filters + Sort + View */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Select value={selectedGenre} onValueChange={setSelectedGenre}>
            <SelectTrigger className="w-[130px] h-9 bg-secondary border-border text-sm" data-testid="genre-filter">
              <Filter className="w-3.5 h-3.5 mr-1.5" />
              <SelectValue placeholder="Genre" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Genres</SelectItem>
              <SelectItem value="Rock">Rock</SelectItem>
              <SelectItem value="Pop">Pop</SelectItem>
              <SelectItem value="Jazz">Jazz</SelectItem>
              <SelectItem value="Electronic">Electronic</SelectItem>
              <SelectItem value="Hip Hop">Hip-Hop</SelectItem>
              <SelectItem value="Funk / Soul">Soul / Funk</SelectItem>
              <SelectItem value="Metal">Metal</SelectItem>
              <SelectItem value="Classical">Classical</SelectItem>
              <SelectItem value="Blues">Blues</SelectItem>
              <SelectItem value="Folk, World, & Country">Folk / Country</SelectItem>
            </SelectContent>
          </Select>

          {/* Condition Filter */}
          <Select value={selectedCondition} onValueChange={setSelectedCondition}>
            <SelectTrigger className="w-[120px] h-9 bg-secondary border-border text-sm" data-testid="condition-filter">
              <SelectValue placeholder="Condition" />
            </SelectTrigger>
            <SelectContent>
              {conditionOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Value Filter Popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                className={`h-9 gap-1.5 text-sm ${(minValue || maxValue) ? 'border-primary' : ''}`}
                data-testid="value-filter-btn"
              >
                <SlidersHorizontal className="w-3.5 h-3.5" />
                Value
                {(minValue || maxValue) && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                    {minValue && maxValue ? `$${minValue}-${maxValue}` : minValue ? `≥$${minValue}` : `≤$${maxValue}`}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72" align="start">
              <div className="space-y-4">
                <h4 className="font-medium">Filter by market value</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="min-value" className="text-xs text-muted-foreground">Min value ($)</Label>
                    <Input
                      id="min-value"
                      type="number"
                      placeholder="0"
                      value={minValue}
                      onChange={(e) => setMinValue(e.target.value)}
                      className="h-9 mt-1"
                      data-testid="min-value-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="max-value" className="text-xs text-muted-foreground">Max value ($)</Label>
                    <Input
                      id="max-value"
                      type="number"
                      placeholder="∞"
                      value={maxValue}
                      onChange={(e) => setMaxValue(e.target.value)}
                      className="h-9 mt-1"
                      data-testid="max-value-input"
                    />
                  </div>
                </div>
                {(minValue || maxValue) && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full"
                    onClick={() => { setMinValue(''); setMaxValue(''); }}
                  >
                    Reset value filter
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>

          <div className="h-9 w-px bg-border mx-1 hidden sm:block" />

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[150px] h-9 bg-secondary border-border text-sm" data-testid="sort-select">
              <SortAsc className="w-3.5 h-3.5 mr-1.5" />
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="artist">Artist (last name)</SelectItem>
              <SelectItem value="artist_first">Artist (first name)</SelectItem>
              <SelectItem value="title">Title</SelectItem>
              <SelectItem value="year">Year</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            data-testid="sort-order-btn"
          >
            <SortAsc className={`w-4 h-4 transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`} />
          </Button>

          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={toggleViewMode}
            data-testid="view-toggle-btn"
            title={viewMode === 'grid' ? 'Switch to list view' : 'Switch to grid view'}
          >
            {viewMode === 'grid' ? <List className="w-4 h-4" /> : <LayoutGrid className="w-4 h-4" />}
          </Button>
        </div>

        {/* Discogs Warning */}
        {!discogsStatus.ok && (
          <div className="mb-8 p-4 bg-destructive/10 border border-destructive/20 rounded-sm">
            <div className="flex items-center gap-2 text-destructive">
              <XCircle className="w-5 h-5" />
              <span className="font-medium">Discogs not connected</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {discogsStatus.message}. Album search features are unavailable.
            </p>
          </div>
        )}

        {/* Collection Grid/List */}
        {loading ? (
          <div className={viewMode === 'grid' ? 'vinyl-grid' : 'space-y-2'}>
            {[...Array(8)].map((_, i) => (
              viewMode === 'grid' ? (
                <div key={i} className="album-card">
                  <div className="skeleton aspect-square" />
                  <div className="p-4 space-y-2">
                    <div className="skeleton h-4 w-3/4" />
                    <div className="skeleton h-3 w-1/2" />
                  </div>
                </div>
              ) : (
                <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-card border border-border">
                  <div className="skeleton w-12 h-12 rounded" />
                  <div className="flex-1 space-y-2">
                    <div className="skeleton h-4 w-1/3" />
                    <div className="skeleton h-3 w-1/4" />
                  </div>
                </div>
              )
            ))}
          </div>
        ) : filteredCollection.length === 0 ? (
          <div className="empty-state">
            <img src="/logo-icon.svg" alt="" className="w-16 h-16 opacity-50 mb-4" />
            <h3 className="text-xl font-semibold mb-2">
              {searchQuery ? 'Ingen albums fundet' : 'Your collection is empty'}
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchQuery ? 'Try a different search' : 'Start by adding your first record'}
            </p>
            {!searchQuery && discogsStatus.ok && (
              <Button
                onClick={() => setShowAddModal(true)}
                className="gap-2 bg-primary text-primary-foreground"
                data-testid="empty-add-album-btn"
              >
                <Plus className="w-4 h-4" />
                Add your first record
              </Button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="vinyl-grid" data-testid="album-grid">
            {filteredCollection.map((item, index) => (
              <ReleaseCard
                key={item.release?.discogs_release_id || index}
                release={item.release}
                copyCount={item.copy_count}
                copies={item.copies}
                onClick={() => navigate(`/release/${item.release?.discogs_release_id}`)}
                style={{ animationDelay: `${index * 0.05}s` }}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-2" data-testid="album-list">
            {filteredCollection.map((item, index) => (
              <div
                key={item.release?.discogs_release_id || index}
                className="flex items-center gap-4 p-3 rounded-lg bg-card border border-border hover:bg-secondary/50 cursor-pointer transition-colors"
                onClick={() => navigate(`/release/${item.release?.discogs_release_id}`)}
                style={{ animationDelay: `${index * 0.02}s` }}
                data-testid={`list-item-${item.release?.discogs_release_id}`}
              >
                <div className="w-14 h-14 rounded overflow-hidden flex-shrink-0 bg-secondary">
                  {item.release?.cover_image_url ? (
                    <img
                      src={item.release.cover_image_url}
                      alt={item.release.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <img src="/logo-icon.svg" alt="" className="w-8 h-8 opacity-30" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium truncate">{item.release?.title || 'Unknown Title'}</h3>
                  <p className="text-sm text-muted-foreground truncate">{item.release?.artist || 'Unknown Artist'}</p>
                </div>
                <div className="hidden sm:block text-sm text-muted-foreground">
                  {item.release?.year || '-'}
                </div>
                <div className="hidden md:block text-sm text-muted-foreground w-24 truncate">
                  {item.release?.label || '-'}
                </div>
                {item.copy_count > 1 && (
                  <Badge variant="secondary" className="flex-shrink-0">
                    <Copy className="w-3 h-3 mr-1" />
                    {item.copy_count}
                  </Badge>
                )}
                <div className="text-sm font-medium w-16 text-right">
                  {item.release?.market_value_median 
                    ? formatCurrency(item.release.market_value_median, item.release.market_value_currency)
                    : '-'}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Add Album Modal */}
      <AddAlbumModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAlbumAdded}
        discogsConnected={discogsStatus.ok}
      />
    </div>
  );
}
